<template>
  <CollectionBaseLayer v-if="data.blData.blFields" :data="data" />
  <div id="cl-body" class="grid cl_body">
    <CollectionCompare
      v-if="compareProducts.length"
      :products="compareProducts"
      @on-compare-click="onCompareClick"
      @on-compare-clear="onCompareClear"
    />
    <div class="row">
      <div class="col-12 col-tl-3 cl__left">
        <!-- Collection Breadcrumb -->
        <nav v-if="!data.blData.blFields" class="cl_breadcrumb" aria-label="Breadcrumb"><a href="/">Home</a> / {{ data.title }}</nav>

        <!-- Collection Title: the page's only H1, visible on mobile and visually hidden on desktop -->
        <h1 class="cl_title cl_title--h1">{{ data.title }}</h1>

        <!-- Collection Filters -->
        <CollectionFilters
          v-if="!productLoader"
          :isFilterDrawerOpen="isFilterDrawerOpen"
          :algoliaFacetData="algoliaFacetData"
          :appliedFilters="appliedFilters"
          :appliedFiltersCount="appliedFiltersCount"
          :priceRange="priceRange"
          :activePriceRange="activePriceRange"
          @updateAppliedFilters="updateAppliedFilters"
          @toggleFilterDrawer="toggleFilterDrawer"
          @clearFilter="clearFilter"
          @priceChange="onPriceChange"
        />
        <div v-else class="cph__filters placeholder"></div>
      </div>
      <div class="col-12 col-tl-9 cl__right">
        <!-- Collection Type Filters -->
        <CollectionTypeFilters
          :filterOptions="typeFilterOptions"
          :appliedFilters="appliedFilters && appliedFilters['type']"
          @updateAppliedFilters="updateAppliedFilters"
        />

        <div class="cl_title-sorting-wrapper">
          <!-- Filter By Button Mobile -->
          <button
            @click="toggleFilterDrawer(true)"
            class="btn btn--white btn--filter cl__filter-btn cl__filter-btn--mobile"
          >
            <span>Filter by</span>
            <svg class="cl_arrow-next">
              <use xlink:href="#icon-next"></use>
            </svg>
          </button>

          <!-- Desktop Title (visual duplicate of the H1) -->
          <p class="cl_title is--desktop" aria-hidden="true">{{ data.title }}</p>

          <!-- Sort By Button -->
          <div class="cl__sortwrapper">
            <label class="cl__sortLabel is--desktop" for="sortOrder">Sort by</label>
            <div class="cl__sortInner btn btn--white btn--filter">
              <select
                id="sortOrder"
                v-model="sortOrder"
                @change="getSortedProduct($event)"
                class="cl__sortSelect"
                name="sortOrder"
              >
                <option v-for="(text, value) in sortOptions" :key="value" :value="value">
                  {{ text }}
                </option>
              </select>
              <svg class="cl_arrow-next">
                <use xlink:href="#icon-next"></use>
              </svg>
            </div>
          </div>
        </div>

        <!-- Product Count String -->
        <div class="cl_product-count">{{ (filterLoading || productLoader) ? '' : productCountString }}</div>

        <!-- Applied Filters Module -->
        <CollectionAppliedFilters
          v-if="appliedFiltersCount > 0"
          :filters="appliedFilters"
          @remove-filter="removeFilter"
          @clear-filter="clearFilter"
        />

        <!-- Products Module -->
        <div
          v-if="!productLoader"
          :class="['cl_products', { 'cl_products--loading': filterLoading }]"
          :data-insights-index="currentIndexName"
        >
          <template v-for="product in displayProducts" :key="product.handle">
            <div
              v-if="product.product_type != 'card'"
              :data-insights-object-id="product.__algoliaObjectID || ''"
              :data-insights-position="product.__algoliaPosition || ''"
              :data-insights-query-id="currentQueryID || ''"
              @click="onProductClick(product)"
            >
              <ProductCard
                type="TypeA"
                :isSecondTurn="isSecondTurn"
                :loadMoreProducts="loadMoreProducts"
                :product="product"
                :callouts="data.callouts"
                :quickview="true"
                :show-discount-badge="true"
                :showCompare="true"
                :compareProducts="compareProducts"
                :selectedColorValue="getProductSwatchColor(product)"
                gtmTrackId="PLP: Product Card"
                :collectionHandle="data.handle"
                :collectionTitle="data.title"
                @on-compare-click="onCompareClick"
              />
            </div>
            <ProductGridCard v-else :data="product" />
          </template>
        </div>
        <div v-else class="cph__products">
          <svg v-for="index in 12" :key="index" class="cph__product">
            <use xlink:href="#icon-product-loader"></use>
          </svg>
        </div>

        <!-- Load More button -->
        <div ref="bottomWrapper" class="cl_bottom">
          <div class="cl_product-count">{{ (filterLoading || productLoader) ? '' : productCountString }}</div>
          <button
            v-if="displayProductCount < filteredProducts.length"
            :disabled="loadMoreProducts"
            class="btn btn--white btn--medium"
            @click="loadMore"
          >
            {{ loadMoreProducts ? 'Loading...' : 'Load More' }}
          </button>
          <div></div>
        </div>

        <!-- Collection Intro Copy (also server-rendered in collection-body-skeleton.liquid) -->
        <div v-if="data.description" class="cl_intro" v-html="data.description"></div>

        <!-- SEO Wrapper -->
        <div v-if="data.seoTitle && data.seoData" class="cl_seo-wrapper">
          <h2 class="cl_seo-title">{{ data.seoTitle }}</h2>
          <div
            v-for="(copy, index) in data.seoData"
            :key="index"
            :class="['cl_seo-menu', { active: isAccorExpanded(copy.heading_new) }]"
          >
            <div
              @click="toggleAccorExpansion(copy.heading_new)"
              @keydown="(e) => handleKeyDownOnSEOAccorClick(e, copy.heading_new)"
              class="cl_seo-menu-title-wrapper"
              tabindex="0"
              role="button"
              :aria-expanded="isAccorExpanded(copy.heading_new)"
              :aria-controls="`seo-content-${index}`"
              aria-live="polite"
            >
              <div class="cl_seo-menu-title" v-html="copy.heading_new"></div>
              <svg class="cl_seo-arrow">
                <use xlink:href="#icon-prev"></use>
              </svg>
            </div>
            <div
              class="cl_seo-menu-content"
              v-html="copy.description_new"
              :id="`seo-content-${index}`"
              role="region"
              :aria-labelledby="`seo-heading-${index}`"
              :aria-hidden="!isAccorExpanded(copy.heading_new)"
              aria-live="assertive"
              ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, onMounted, reactive, onUnmounted } from 'vue';
  import { useStore } from 'vuex';
  import CollectionFilters from './components/collection-filters.vue';
  import CollectionAppliedFilters from './components/collection-applied-filters.vue';
  import CollectionTypeFilters from './components/collection-type-filters.vue';
  import CollectionBaseLayer from './components/collection-baselayer.vue';
  import CollectionCompare from './components/collection-compare.vue';
  import ProductCard from '../global/product-card/index.vue';
  import ProductGridCard from '../global/product-grid-card.vue';
  import urlSearchParams from './composables/urlSeachParams';
  import useSorting from './composables/useSorting';
  import isEqual from 'lodash/isEqual';
  import {
    fetchAllCollectionProducts,
    fetchCollectionProducts,
  } from '../../search/algolia/collection-fetcher';
  import { matchProducts } from '../../search/algolia/product-matcher';
  import { getCustomerDiscountValue, sortByCustomerPrice } from '../../search/algolia/customer-pricing';
  import { getCardDisplayPrice } from '../global/product-card/card-pricing';
  import { getOrderedFacets } from '../../search/algolia/facet-ordering';
  import {
    buildFacetFilters,
    buildDynamicAttributeMap,
  } from '../../search/algolia/facet-filter-builder';

  const props = defineProps({
    data: {
      type: Object,
      required: true,
    },
  });

  const { data } = toRefs(props);

  const store = useStore();
  const overlay = new window.Overlay();
  const isFilterDrawerOpen = ref(false);
  const collectionData = ref(data.value);
  const appliedFilters = ref({});
  const displayProductCount = ref(12);
  const bottomWrapper = ref(null);
  const loadMoreProducts = ref(false);
  const productLoader = ref(false);
  const filterLoading = ref(false);
  const expandedAccor = reactive([]);
  const compareProducts = ref([]);
  const originalLiquidProducts = ref([]);
  const algoliaFacetData = ref([]);
  const dynamicAttributeMap = ref({});
  const priceRange = ref({ min: 0, max: 1000 });
  const priceRangeInitialized = ref(false);
  const activePriceRange = ref([]);
  const currentQueryID = ref(null);
  const currentIndexName = ref(null);
  const perFacetCache = ref({});

  const { params, addSearchParam } = urlSearchParams();

  const swatchColorParam = params.value && params.value['f-swatch-color'];
  const swatchColorFilter = ref(
    swatchColorParam ? decodeURIComponent(swatchColorParam).replace(/-/g, ' ') : ''
  );

  const { sortOptions, sortOrder, sortOrderDisplayNames } = useSorting({
    searchParams: params,
    addSearchParam,
  });

  let querySequence = 0;
  let lastViewedQueryID = null;

  onMounted(async () => {
    store.dispatch('fetchWishlistItems');
    productCountParam();

    originalLiquidProducts.value = [...collectionData.value.products];
    productLoader.value = true;

    if (hasUrlFilters()) {
      await primeFacetState();
    }

    restoreFiltersFromUrl();

    await queryAlgolia({
      sortKey: sortOrderDisplayNames[sortOrder.value],
      facetFilters: buildAlgoliaFacetFilters(),
      numericFilters: buildNumericFilters(activePriceRange.value),
    });

    productLoader.value = false;
    loaderObserver();
    refreshYotpo();
    window.addEventListener('refreshWishlist', refreshWishlist);
  });

  onUnmounted(() => {
    window.removeEventListener('refreshWishlist', refreshWishlist);
  });

  const refreshWishlist = () => {
    store.dispatch('fetchWishlistItems');
  };

  const isSecondTurn = computed(() => {
    // The Second Turn template can be assigned to collections whose handle doesn't say so
    const { handle, isSecondTurnTemplate } = collectionData.value;
    return isSecondTurnTemplate || handle.toLowerCase().includes('second-turn');
  });

  const evenPage = computed(() => {
    return (displayProductCount.value / 12) % 2;
  });

  const productCountString = computed(() => {
    const selectedCount = displayProducts.value.filter((p) => p.product_type !== 'card').length;
    const totalProducts = filteredProducts.value.length;

    if (selectedCount && totalProducts) {
      const countString = totalProducts > 12 ? `-${selectedCount}` : `-${totalProducts}`;
      const itemsString = totalProducts > 1 ? 'items' : 'item';

      return `1${countString} of ${totalProducts} ${itemsString}`;
    } else {
      return '';
    }
  });

  const filteredProducts = computed(() => {
    const { products } = collectionData.value;

    let result = [...products];

    if (swatchColorFilter.value) {
      result = result.filter((product) =>
        product.variants.some(
          ({ available, color }) =>
            available && color && color.toLowerCase() === swatchColorFilter.value.toLowerCase()
        )
      );
    }

    return result;
  });

  const getProductSwatchColor = (product) => {
    if (!swatchColorFilter.value) return '';
    const matchingVariant = product.variants.find(
      (v) => v.option1 && v.option1.toLowerCase() === swatchColorFilter.value.toLowerCase()
    );
    return matchingVariant ? matchingVariant.option1 : '';
  };

  const displayProducts = computed(() => {
    let filtered = filteredProducts.value;

    if (appliedFiltersCount.value > 0) {
      const { colors } = collectionData.value;
      Object.entries(appliedFilters.value).forEach(([key, filterOptions]) => {
        if (key === 'color' && filterOptions.length) {
          const colorData = filterOptions.flatMap(
            (option) => colors.find((color) => color.colorName.toLowerCase() === option.toLowerCase())?.swatchNames.split(',') || []
          );

          filtered = filteredProducts.value.reduce((products, product) => {
            const matchingVariants = product.variants.filter(
              ({ available, color }) => available && colorData.includes(color.toLowerCase())
            );
            if (matchingVariants.length) {
              products.push({ ...product, variants: matchingVariants });
            }
            return products;
          }, []);
        }
      });
    }

    const sliced = filtered.slice(0, displayProductCount.value);
    if (appliedFiltersCount.value > 0 || sliced.length === 0) return sliced;
    return insertGridCards(sliced);
  });

  const appliedFiltersCount = computed(() =>
    Object.values(appliedFilters.value).reduce((count, val) => count + val.length, 0)
  );

  const typeFilterOptions = computed(() => {
    const typeFacet = algoliaFacetData.value.find((f) => f.filterKey === 'type');
    return typeFacet ? typeFacet.values : {};
  });

  const toggleFilterDrawer = (value) => {
    let bodyStyle = document.body.style;
    if (value) {
      isFilterDrawerOpen.value = true;
      overlay.show();
      bodyStyle.overflow = 'hidden';
    } else {
      isFilterDrawerOpen.value = false;
      overlay.hide();
      bodyStyle.overflow = 'auto';
    }
  };

  overlay.on('cancel', () => {
    toggleFilterDrawer(false);
  });

  const updateAppliedFilters = async (filterKey, options) => {
    appliedFilters.value = {
      ...appliedFilters.value,
      [filterKey]: options.map((name) => name),
    };

    addSearchParam(`f-${filterKey}`, options.map((name) => name).join('~'));

    displayProductCount.value = 12;
    updateCountParam(12);
    filterLoading.value = true;

    await queryAlgolia({
      sortKey: sortOrderDisplayNames[sortOrder.value],
      facetFilters: buildAlgoliaFacetFilters(),
      numericFilters: buildNumericFilters(activePriceRange.value),
    });

    filterLoading.value = false;
    refreshYotpo();
  };

  const removeFilter = async (filterKey, option) => {
    if (filterKey === 'price') {
      activePriceRange.value = [];
      const { price, ...rest } = appliedFilters.value;
      appliedFilters.value = rest;
      addSearchParam('f-price', '');
    } else {
      appliedFilters.value[filterKey] = (appliedFilters.value[filterKey] || []).filter((filter) => filter !== option);
      addSearchParam(`f-${filterKey}`, appliedFilters.value[filterKey].join('~'));
    }

    displayProductCount.value = 12;
    updateCountParam(12);
    filterLoading.value = true;

    await queryAlgolia({
      sortKey: sortOrderDisplayNames[sortOrder.value],
      facetFilters: buildAlgoliaFacetFilters(),
      numericFilters: buildNumericFilters(activePriceRange.value),
    });

    filterLoading.value = false;
    refreshYotpo();
  };

  const clearFilter = async () => {
    Object.keys(appliedFilters.value).forEach((key) => addSearchParam(`f-${key}`, ''));
    appliedFilters.value = {};
    activePriceRange.value = [];
    addSearchParam('f-price', '');

    displayProductCount.value = 12;
    updateCountParam(12);
    filterLoading.value = true;

    await queryAlgolia({
      sortKey: sortOrderDisplayNames[sortOrder.value],
    });

    filterLoading.value = false;
    refreshYotpo();
  };

  const onPriceChange = async (range) => {
    activePriceRange.value = range;

    if (range.length) {
      appliedFilters.value = { ...appliedFilters.value, price: [`$${range[0]} - $${range[1]}`] };
      addSearchParam('f-price', `${range[0]}~${range[1]}`);
    } else {
      const { price, ...rest } = appliedFilters.value;
      appliedFilters.value = rest;
      addSearchParam('f-price', '');
    }

    displayProductCount.value = 12;
    updateCountParam(12);
    filterLoading.value = true;

    await queryAlgolia({
      sortKey: sortOrderDisplayNames[sortOrder.value],
      facetFilters: buildAlgoliaFacetFilters(),
      numericFilters: buildNumericFilters(activePriceRange.value),
    });

    filterLoading.value = false;
    refreshYotpo();
  };

  const onProductClick = (product) => {
    if (!product.__algoliaObjectID || !currentIndexName.value) return;

    const attribution = {
      objectID: product.__algoliaObjectID,
      index: currentIndexName.value,
      queryID: currentQueryID.value,
      position: product.__algoliaPosition,
    };

    if (typeof window.setAlgoliaSearchAttribution === 'function') {
      window.setAlgoliaSearchAttribution(attribution);
    }
  };

  const buildAlgoliaFacetFilters = () => {
    const filters = expandColorFiltersForAlgolia(appliedFilters.value);
    return buildFacetFilters(filters, dynamicAttributeMap.value);
  };

  const expandColorFiltersForAlgolia = (filters) => {
    const colorAttribute = dynamicAttributeMap.value.color;
    if (colorAttribute !== 'options.color') return filters;

    const colorValues = filters.color;
    if (!colorValues?.length) return filters;

    const { colors } = collectionData.value;
    if (!colors?.length) return filters;

    const expandedColors = [];
    for (const groupName of colorValues) {
      const colorEntry = colors.find(
        (c) => c.colorName.toLowerCase() === groupName.toLowerCase()
      );
      if (colorEntry?.swatchNames) {
        const names = colorEntry.swatchNames
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean);
        expandedColors.push(...names);
      } else {
        expandedColors.push(groupName);
      }
    }

    return { ...filters, color: expandedColors };
  };

  const buildNumericFilters = (priceRangeVal) => {
    if (!priceRangeVal || !priceRangeVal.length) return [];
    return [`price>=${priceRangeVal[0]}`, `price<=${priceRangeVal[1]}`];
  };

  const insertGridCards = (products) => {
    const { inGridCards } = collectionData.value;
    if (!inGridCards?.length) return products;

    const result = [...products];
    const sorted = [...inGridCards].sort((a, b) => a.gridPosition - b.gridPosition);

    for (const card of sorted) {
      const pos = Math.min(card.gridPosition - 1, result.length);
      result.splice(pos, 0, { ...card, product_type: 'card' });
    }

    return result;
  };

  const loaderObserver = () => {
    const options = {
      rootMargin: '100px',
      threshold: 0.25,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (evenPage.value && filteredProducts.value.length > 12) {
            loadMore();
          }
          if (displayProductCount.value === filteredProducts.value.length) {
            observer.unobserve(bottomWrapper.value);
          }
        }
      });
    }, options);

    observer.observe(bottomWrapper.value);
  };

  const hasUrlFilters = () => {
    if (!params.value) return false;
    return Object.keys(params.value).some((key) => key.startsWith('f-') && key !== 'f-swatch-color');
  };

  /**
   * Filters restored from the URL are applied before any facet data exists, so
   * the attribute behind each filter key is unknown. Color is the trap: most
   * collections facet on meta.custom.color_group, but Second Turn records have
   * no color group and fall back to options.color. Guessing wrong returns zero
   * hits and an empty grid. One unfiltered, hitless query resolves the attribute
   * map and seeds the sibling-value cache and the full price range, exactly as
   * if the shopper had applied the filters on the page.
   */
  const primeFacetState = async () => {
    try {
      const result = await fetchCollectionProducts({
        collectionHandle: data.value.handle,
        hitsPerPage: 0,
        sortKey: sortOrderDisplayNames[sortOrder.value],
        ruleContexts: isSecondTurn.value ? ['second_turn_template'] : [],
      });

      const orderedFacets = getOrderedFacets({
        facets: result.facets,
        renderingContent: result.renderingContent,
      });

      for (const facet of orderedFacets) {
        perFacetCache.value[facet.filterKey] = facet.values;
      }
      algoliaFacetData.value = transformFacetDataForUI(orderedFacets);
      dynamicAttributeMap.value = buildDynamicAttributeMap(orderedFacets);

      if (result.facets_stats?.price) {
        priceRange.value = {
          min: result.facets_stats.price.min,
          max: result.facets_stats.price.max,
        };
        priceRangeInitialized.value = true;
      }
    } catch (error) {
      console.error('[Collection] Algolia facet priming failed:', error);
    }
  };

  const restoreFiltersFromUrl = () => {
    if (!params.value) return;

    Object.entries(params.value)
      .filter(([key]) => key.startsWith('f-') && key !== 'f-swatch-color')
      .forEach(([key, value]) => {
        const values = value.split('~').filter(Boolean);
        if (!values.length) return;
        const filterKey = key.slice(2);

        if (filterKey === 'price' && values.length === 2) {
          const min = Number(values[0]);
          const max = Number(values[1]);
          if (!isNaN(min) && !isNaN(max)) {
            activePriceRange.value = [min, max];
            appliedFilters.value = {
              ...appliedFilters.value,
              price: [`$${min} - $${max}`],
            };
          }
        } else {
          appliedFilters.value = {
            ...appliedFilters.value,
            [filterKey]: values,
          };
        }
      });
  };

  const productCountParam = () => {
    var params = new window.URLSearchParams(window.location.search);
    var countParam = params.get('count');

    if (countParam) {
      displayProductCount.value = Number(countParam) || 12;
    } else {
      updateCountParam(12);
    }
  };

  const updateCountParam = (value) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    params.set('count', value);

    url.search = params.toString();
    window.history.replaceState({ url: url.toString() }, null, url.toString());
  };

  const loadMore = () => {
    loadMoreProducts.value = true;
    const newCount = displayProductCount.value + 12;

    if (newCount < filteredProducts.value.length) {
      displayProductCount.value = newCount;
    } else {
      displayProductCount.value = filteredProducts.value.length;
    }

    updateCountParam(displayProductCount.value);
    refreshYotpo();

    setTimeout(() => {
      loadMoreProducts.value = false;
    }, 1000);
  };


  /**
   * Price sorts are re-sorted by the price each card will show, for every
   * customer: the replicas sort on whichever variant Algolia picked, while the
   * card prices off its selected color, and discounted customers (PRO/FF/EMP)
   * see tier prices the replicas don't know about.
   * Insights positions are renumbered to the new slots when the order changes.
   */
  const sortByCustomerPriceShown = (products, indexName) => {
    const sorted = sortByCustomerPrice(products, indexName, (product) => {
      const discount = getCustomerDiscountValue(product.tags);
      return {
        discount,
        price: getCardDisplayPrice(product, {
          selectedColorValue: getProductSwatchColor(product),
          customerDiscountValue: discount,
          showDiscountBadge: true,
          isSecondTurn: isSecondTurn.value,
        }),
      };
    }, { requireDiscount: false });

    if (sorted === products) return products;
    return sorted.map((product, index) => ({ ...product, __algoliaPosition: index + 1 }));
  };

  /**
   * Queries Algolia for collection products and updates the UI.
   * Central function for all data fetching — initial load, sorting, and filtering.
   */
  const queryAlgolia = async ({ sortKey = 'featured', facetFilters = [], numericFilters = [] } = {}) => {
    const thisQuery = ++querySequence;

    try {
      const result = await fetchAllCollectionProducts({
        collectionHandle: data.value.handle,
        sortKey,
        facetFilters,
        numericFilters,
        ruleContexts: isSecondTurn.value ? ['second_turn_template'] : [],
      });

      if (thisQuery !== querySequence) return;

      let products;
      const hasActiveFilters = facetFilters.length > 0 || numericFilters.length > 0;

      if (result.hits.length > 0) {
        const reordered = sortByCustomerPriceShown(
          matchProducts(result.hits, originalLiquidProducts.value),
          result.index
        );

        if (reordered.length > 0) {
          products = reordered;
        } else if (hasActiveFilters) {
          products = [];
        } else {
          products = [...originalLiquidProducts.value];
        }
      } else if (hasActiveFilters) {
        products = [];
      } else {
        products = [...originalLiquidProducts.value];
      }

      collectionData.value.products = products.filter(
        (product) => product.available || product.tags.some((tag) => tag.startsWith('bis'))
      );

      const orderedFacets = getOrderedFacets({
        facets: result.facets,
        renderingContent: result.renderingContent,
      });

      // A zero-hit filtered response carries no facets. Rebuilding from it would
      // empty the sidebar and reset the attribute map, so the next query would
      // target the wrong color attribute too. Keep the last good facet state.
      const keepFacetState = !orderedFacets.length && hasActiveFilters;

      // Update cache for every facet that does NOT have an active filter.
      // This keeps each facet's cache at "what it looked like just before you touched it."
      for (const facet of orderedFacets) {
        if (!appliedFilters.value[facet.filterKey]?.length) {
          perFacetCache.value[facet.filterKey] = facet.values;
        }
      }

      // For facets with an active filter, swap in the cached values so siblings stay visible.
      const stableFacets = orderedFacets.map((facet) => {
        if (appliedFilters.value[facet.filterKey]?.length && perFacetCache.value[facet.filterKey]) {
          return { ...facet, values: perFacetCache.value[facet.filterKey] };
        }
        return facet;
      });

      if (!keepFacetState) {
        algoliaFacetData.value = transformFacetDataForUI(stableFacets);
        dynamicAttributeMap.value = buildDynamicAttributeMap(stableFacets);
      }

      currentQueryID.value = result.queryID || null;
      currentIndexName.value = result.index || null;

      if (result.queryID && result.queryID !== lastViewedQueryID) {
        lastViewedQueryID = result.queryID;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'Hits Viewed' });
      }

      if (result.facets_stats && result.facets_stats.price && !priceRangeInitialized.value) {
        priceRange.value = {
          min: result.facets_stats.price.min,
          max: result.facets_stats.price.max,
        };
        priceRangeInitialized.value = true;
      }
    } catch (error) {
      if (thisQuery !== querySequence) return;
      console.error('[Collection] Algolia fetch failed, using Liquid product order:', error);
      collectionData.value.products = originalLiquidProducts.value.filter(
        (product) => product.available || product.tags.some((tag) => tag.startsWith('bis'))
      );
    }
  };

  /**
   * Transforms facet data for the filter UI.
   * Colors get hex codes from collectionData.colors for swatch rendering.
   */
  const transformFacetDataForUI = (orderedFacets) => {
    const { colors } = collectionData.value;

    return orderedFacets.map((facet) => {
      if (facet.filterKey === 'color' && colors && colors.length) {
        const transform = facet.attribute === 'meta.custom.color_group'
          ? buildColorSwatchValues
          : buildColorSwatchValuesBySwatchNames;
        return { ...facet, values: transform(facet.values, colors) };
      }
      return facet;
    });
  };

  /**
   * Maps Algolia color values to "ColorName/#hexcode" format for swatch rendering.
   * Only includes colors that exist in the collectionData.colors swatch map.
   */
  const buildColorSwatchValues = (algoliaColorValues, colors) => {
    const swatchValues = {};
    const colorLookup = new Map();

    for (const color of colors) {
      colorLookup.set(color.colorName.toLowerCase(), color);
    }

    for (const [colorName, count] of Object.entries(algoliaColorValues)) {
      const colorEntry = colorLookup.get(colorName.toLowerCase());
      if (colorEntry) {
        const key = `${colorEntry.colorName}/${colorEntry.colorCode}`;
        swatchValues[key] = count;
      }
    }

    return swatchValues;
  };

  /**
   * Maps specific options.color values (e.g. "Steel Pony Whitebark Camo")
   * to color groups using the swatchNames field from the Liquid colors map.
   * Sums counts for all specific colors that belong to the same group.
   */
  const buildColorSwatchValuesBySwatchNames = (algoliaColorValues, colors) => {
    const swatchValues = {};
    const swatchNameToColor = new Map();

    for (const color of colors) {
      if (!color.swatchNames) continue;
      const names = color.swatchNames.split(',');
      for (const name of names) {
        const trimmed = name.trim().toLowerCase();
        if (trimmed) swatchNameToColor.set(trimmed, color);
      }
    }

    for (const [colorName, count] of Object.entries(algoliaColorValues)) {
      const colorEntry = swatchNameToColor.get(colorName.toLowerCase());
      if (colorEntry) {
        const key = `${colorEntry.colorName}/${colorEntry.colorCode}`;
        swatchValues[key] = (swatchValues[key] || 0) + count;
      }
    }

    return swatchValues;
  };

  const getSortedProduct = async () => {
    filterLoading.value = true;
    const algoliaKey = sortOrderDisplayNames[sortOrder.value];
    await queryAlgolia({
      sortKey: algoliaKey,
      facetFilters: buildAlgoliaFacetFilters(),
      numericFilters: buildNumericFilters(activePriceRange.value),
    });
    filterLoading.value = false;
    refreshYotpo();
  };

  const refreshYotpo = () => {
    window.refreshYotpoData();
  };

  const isAccorExpanded = (key) => {
    return expandedAccor.indexOf(key) !== -1;
  };

  const toggleAccorExpansion = (key) => {
    if (isAccorExpanded(key)) {
      expandedAccor.splice(expandedAccor.indexOf(key), 1);
    } else {
      expandedAccor.push(key);
    }
  };

  const handleKeyDownOnSEOAccorClick = (e, heading) => {
    if (e.key === 'Enter' || e.key === ' ') {
      toggleAccorExpansion(heading);
      e.preventDefault();
    }
  };

  const onCompareClick = (checked, product) => {
    if (checked) {
      compareProducts.value = [...compareProducts.value, product];
    } else {
      compareProducts.value = compareProducts.value.filter((ele) => !isEqual(ele.id, product.id));
    }
  };

  const onCompareClear = () => {
    compareProducts.value = [];
  };
</script>
